"use client";

import {
  beginVehicleSearchIntent,
  getVehicleSearchOwner,
  subscribeVehicleSearchOwner,
  readVehicleSearchContext,
  saveVehicleSearchContext,
} from "@/lib/vehicle/corporateSearch";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useRouter } from "next-nprogress-bar";
import { addDays, format } from "date-fns";
import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";
import type {
  Airport,
  VehicleRentalLocation,
  VehicleSearchResponse,
} from "@/generated/search.openapi";
import { searchAndBookTestEnv } from "@/lib/apiUtils";
import type { SearchSuggestion } from "../SearchInput";
import type { DateRangeWithFlexibleData } from "../dateRangeWithFlexible/DateRangeWithFlexible";
import { getPlaceDetails } from "@/lib/mapUtils";
import { VehicleRentalLocationFromJSON } from "@/generated/search.openapi";
import {
  useRentalPickupSuggestions,
  type RentalPickupArea,
} from "./useRentalPickupSuggestions";
import { matchExactRentalDesk } from "./rentalDeskMatcher";

import { RENTAL_VENDOR_OPTIONS } from "@/lib/vehicle/rentalVendors";

// --- Shared constants ---

export const DEFAULT_PICKUP_TIME = "10:00";
export const DEFAULT_DROPOFF_TIME = "10:00";

/**
 * Convert a 24-hour `HH:MM` string into a 12-hour `h:mm AM/PM` label.
 * The 24-hour value stays the source of truth on the wire; the label
 * is purely UI-facing so users see standard time, not military time.
 */
export function formatTimeLabel(time: string): string {
  const [hh, mm] = time.split(":");
  const h = parseInt(hh, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${mm} ${ampm}`;
}

export const TIME_OPTIONS: ReadonlyArray<{ value: string; label: string }> =
  Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = i % 2 === 0 ? "00" : "30";
    const hh = hour.toString().padStart(2, "0");
    const value = `${hh}:${minute}`;
    return { value, label: formatTimeLabel(value) };
  });

export type PickupLocationType =
  | "Airport"
  | "RailwayStation"
  | "Hotel"
  | "CityCenterDowntown";

export function resolveLocationType(types: string[]): PickupLocationType {
  if (types.includes("airport")) return "Airport";
  if (types.includes("train_station") || types.includes("transit_station"))
    return "RailwayStation";
  if (types.includes("lodging")) return "Hotel";
  return "CityCenterDowntown";
}

export const VEHICLE_SEARCH_OPTIONS = {
  types: [
    "airport",
    "locality",
    "transit_station",
    "point_of_interest",
    "street_address",
  ],
};

export function formatDateTime(dateStr: string, time: string): string {
  const date = new Date(dateStr + "T00:00:00");
  return `${format(date, "EEE M/d")}  ${formatTimeLabel(time)}`;
}

// --- Hook options ---

interface UseVehicleSearchOptions {
  sourceSearchId?: string;
  initialPickupDisplay?: string;
  initialDropoffDisplay?: string;
  initialPickupDate?: string;
  initialDropoffDate?: string;
  initialPickupTime?: string;
  initialDropoffTime?: string;
  initialOneWay?: boolean;
  /** Pre-resolved IATA code for the pickup location (skips re-resolution) */
  initialPickupIata?: string;
  /** Pre-resolved IATA code for the drop-off location (skips re-resolution) */
  initialDropoffIata?: string;
  /** Pre-resolved free-text/geo query (e.g. "lat,lng") so a header re-search
   *  reproduces the unified city-desk search without re-resolving (GON-5981). */
  initialPickupQuery?: string;
  initialDropoffQuery?: string;
  initialPickupVendorCode?: string;
  initialPickupVendorLocationId?: string;
  /** If provided, called instead of router.push on successful search.
   *  Receives the full result path including any URL params (e.g. dropoff location). */
  onSearchComplete?: (resultPath: string) => void;
}

export type VehicleSearchState = ReturnType<typeof useVehicleSearch>;

const EMPTY_OPTIONS: UseVehicleSearchOptions = {};

export default function useVehicleSearch(
  options: UseVehicleSearchOptions = EMPTY_OPTIONS
) {
  const router = useRouter();
  const posthog = usePostHog();
  const sourceSearchIdRef = useRef(options.sourceSearchId);
  sourceSearchIdRef.current = options.sourceSearchId;
  const submitGeneration = useRef(0);
  const submittingRef = useRef(false);
  const owner = useSyncExternalStore(
    subscribeVehicleSearchOwner,
    getVehicleSearchOwner,
    () => undefined
  );
  const seededOwner = useRef(owner);
  const initialDiscount = useRef(
    options.sourceSearchId
      ? readVehicleSearchContext(options.sourceSearchId)?.discount
      : undefined
  );
  const [discountCode, updateDiscountCode] = useState(
    initialDiscount.current?.code ?? ""
  );
  const [codeVendorCodes, updateCodeVendorCodes] = useState<string[]>(
    initialDiscount.current?.vendorCodes ??
      (initialDiscount.current ? [initialDiscount.current.vendorCode] : [])
  );
  const codeRef = useRef(discountCode);
  const vendorsRef = useRef(codeVendorCodes);
  const setDiscountCode = useCallback((code: string) => {
    codeRef.current = code;
    updateDiscountCode(code);
  }, []);
  const setCodeVendorCodes = useCallback((codes: string[]) => {
    vendorsRef.current = codes;
    updateCodeVendorCodes(codes);
  }, []);
  useEffect(
    () => () => {
      ++submitGeneration.current;
      ++suggestionRefId.current;
      ++dropoffSuggestionRefId.current;
    },
    []
  );
  const onSearchCompleteRef = useRef(options.onSearchComplete);
  onSearchCompleteRef.current = options.onSearchComplete;

  const suggestionRefId = useRef(0);
  const dropoffSuggestionRefId = useRef(0);

  // --- Location state ---
  const [resolvedIata, setResolvedIata] = useState<string | null>(
    options.initialPickupIata ?? null
  );
  const [resolvedCityCode, setResolvedCityCode] = useState<string | null>(null);
  // Free-text/geo query for the unified backend search (GON-5981). For non-airport
  // selections (city/address/POI) we send the resolved "lat,lng" as pickupQuery so
  // the backend resolves the precise point and surfaces CityCenterDowntown city
  // desks near it. Null for direct airport picks (they keep the legacy IATA path).
  const [resolvedPickupQuery, setResolvedPickupQuery] = useState<string | null>(
    options.initialPickupQuery ?? null
  );
  const [pickupDesk, setPickupDesk] = useState<VehicleRentalLocation | null>(
    options.initialPickupVendorCode && options.initialPickupVendorLocationId
      ? ({
          metroCode: options.initialPickupIata,
          vendorCode: options.initialPickupVendorCode,
          vendorLocationId: options.initialPickupVendorLocationId,
        } as VehicleRentalLocation)
      : null
  );
  const [locationType, setLocationType] = useState<PickupLocationType>(
    options.initialPickupQuery || options.initialPickupVendorCode
      ? "CityCenterDowntown"
      : "Airport"
  );
  const [locationDisplay, setLocationDisplay] = useState(
    options.initialPickupDisplay ?? ""
  );
  const [isResolving, setIsResolving] = useState(false);

  const [pickupArea, setPickupArea] = useState<RentalPickupArea | null>(
    options.initialPickupQuery && options.initialPickupIata
      ? {
          iata: options.initialPickupIata,
          cityCode: options.initialPickupIata,
          query: options.initialPickupQuery,
          display: options.initialPickupVendorCode
            ? options.initialPickupIata
            : options.initialPickupDisplay ?? options.initialPickupIata,
          type: "CityCenterDowntown",
        }
      : null
  );
  const selectPickupBranch = useCallback(
    (desk: VehicleRentalLocation, area = pickupArea) => {
      if (!area) return;
      setPickupArea(area);
      ++suggestionRefId.current;
      setResolvedIata(desk.metroCode);
      setResolvedCityCode(desk.metroCode);
      setResolvedPickupQuery(`${desk.latitude},${desk.longitude}`);
      setLocationType("CityCenterDowntown");
      setLocationDisplay(`${desk.vendorName} — ${desk.address}`);
      setPickupDesk(desk);
      setIsResolving(false);
      setError(null);
    },
    [pickupArea]
  );

  const clearPickupBranch = useCallback(() => {
    if (!pickupArea) return;
    ++suggestionRefId.current;
    setResolvedIata(pickupArea.iata);
    setResolvedCityCode(pickupArea.cityCode);
    setResolvedPickupQuery(pickupArea.query);
    setLocationType(pickupArea.type);
    setLocationDisplay(pickupArea.display);
    setPickupDesk(null);
    setIsResolving(false);
    setError(null);
  }, [pickupArea]);

  const loadPickupSuggestions = useRentalPickupSuggestions({
    area: pickupArea,
    onSelect: selectPickupBranch,
  });

  const seedPickupSuggestions = useMemo(
    () =>
      pickupArea
        ? [
            {
              key: `area:${pickupArea.query}`,
              description: `All locations near ${pickupArea.display}`,
              onSelect: () => {
                clearPickupBranch();
              },
            },
          ]
        : [],
    [pickupArea, clearPickupBranch]
  );

  // --- One-way dropoff state ---
  const [oneWay, setOneWay] = useState(options.initialOneWay ?? false);
  const [resolvedDropoffIata, setResolvedDropoffIata] = useState<string | null>(
    options.initialDropoffIata ?? null
  );
  const [resolvedDropoffCityCode, setResolvedDropoffCityCode] = useState<
    string | null
  >(null);
  const [resolvedDropoffQuery, setResolvedDropoffQuery] = useState<
    string | null
  >(options.initialDropoffQuery ?? null);
  const [dropoffLocationType, setDropoffLocationType] =
    useState<PickupLocationType>("Airport");
  const [dropoffLocationDisplay, setDropoffLocationDisplay] = useState(
    options.initialDropoffDisplay ?? ""
  );
  const [isResolvingDropoff, setIsResolvingDropoff] = useState(false);

  // --- Dates & times ---
  const defaultPickupDate =
    options.initialPickupDate || format(addDays(new Date(), 7), "yyyy-MM-dd");
  const defaultDropoffDate =
    options.initialDropoffDate || format(addDays(new Date(), 10), "yyyy-MM-dd");

  const [dates, setDates] = useState<DateRangeWithFlexibleData>({
    type: "date",
    date: {
      startDateStr: defaultPickupDate,
      endDateStr: defaultDropoffDate,
    },
  });

  const [pickupTime, setPickupTime] = useState(
    options.initialPickupTime || DEFAULT_PICKUP_TIME
  );
  const [dropoffTime, setDropoffTime] = useState(
    options.initialDropoffTime || DEFAULT_DROPOFF_TIME
  );

  // --- Submission state ---
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sameDayDisabled = !!useFeatureFlagEnabled(
    "rental-car-same-day-disabled"
  );

  // --- Airport resolution ---

  const resolveAirport = useCallback(
    async (
      suggestion: SearchSuggestion | null,
      refId: React.MutableRefObject<number>,
      setIata: (v: string | null) => void,
      setCityCode: (v: string | null) => void,
      setDisplay: (v: string) => void,
      setLocType: (v: PickupLocationType) => void,
      setResolving: (v: boolean) => void,
      setQuery: (v: string | null) => void,
      setDesk: (v: VehicleRentalLocation | null) => void
    ) => {
      const thisId = ++refId.current;
      setIata(null);
      setCityCode(null);
      setResolving(false);
      if (refId === suggestionRefId) {
        setPickupArea(null);
      }

      if (!suggestion) {
        setIata(null);
        setCityCode(null);
        setDisplay("");
        setLocType("Airport");
        setQuery(null);
        setDesk(null);
        return;
      }

      const locType = resolveLocationType(suggestion.types);
      const isAirport = locType === "Airport";

      setDisplay(suggestion.description);
      setLocType(locType);
      setError(null);
      setResolving(true);
      // Clear any prior geo query up front so a lookup that fails partway can't
      // leave a stale pickupQuery paired with an old IATA (GON-5981 BugBot). It's
      // only re-set once the nearby-airport lookup succeeds, in lockstep with the
      // resolved code below.
      setQuery(null);
      setDesk(null);

      try {
        const placeResult = await getPlaceDetails(
          suggestion.placeId,
          ["geometry"],
          suggestion.sessionToken
        );
        if (thisId !== refId.current) return;

        const location = placeResult?.geometry?.location;
        if (!location) {
          setError(
            "We couldn't pinpoint that location. Try searching for an airport or a nearby city."
          );
          setResolving(false);
          return;
        }

        const { lat, lng } = location.toJSON();
        const isRentalDeskPick = suggestion.types.includes("car_rental");
        const [res, deskRes] = await Promise.all([
          fetch(`/api/vehicle/airports?lat=${lat}&lng=${lng}`),
          isRentalDeskPick
            ? fetch(`/api/vehicle/locations?lat=${lat}&lng=${lng}`).catch(
                () => null
              )
            : Promise.resolve(null),
        ]);
        if (thisId !== refId.current) return;

        if (!res.ok) {
          setError(
            isAirport
              ? "We couldn't load airport details. Please try again."
              : "We couldn't find a nearby airport. Try a major city or airport instead."
          );
          setResolving(false);
          return;
        }

        const airports: Airport[] = await res.json();
        const desks: VehicleRentalLocation[] = deskRes?.ok
          ? (await deskRes.json()).map(VehicleRentalLocationFromJSON)
          : [];
        if (thisId !== refId.current) return;

        const nearest = airports[0];
        if (nearest?.iataCode) {
          setIata(nearest.iataCode);
          setCityCode(nearest.iataCityCode ?? null);
          // Non-airport selections drive the unified backend search via a
          // "lat,lng" pickupQuery so city desks near the exact point surface;
          // airport picks keep the legacy IATA-only path. Set in lockstep with
          // the resolved code so the two never disagree (GON-5981).
          setQuery(isAirport ? null : `${lat},${lng}`);
          if (!isAirport && refId === suggestionRefId) {
            setPickupArea({
              iata: nearest.iataCode,
              cityCode: nearest.iataCityCode ?? null,
              query: `${lat},${lng}`,
              display: suggestion.description,
              type: locType,
            });
          }
          if (isRentalDeskPick && desks.length > 0) {
            setDesk(
              matchExactRentalDesk(suggestion.description, lat, lng, desks)
            );
          }
        } else {
          setError(
            isAirport
              ? "We couldn't find this airport in our system. Try a different airport."
              : "No rental car suppliers found near this spot. Try a nearby airport or major city."
          );
        }
      } catch {
        if (thisId !== refId.current) return;
        setError("Something went wrong looking that up. Please try again.");
      } finally {
        if (thisId === refId.current) setResolving(false);
      }
    },
    []
  );

  const handleSuggestionClick = useCallback(
    (suggestion: SearchSuggestion | null) =>
      resolveAirport(
        suggestion,
        suggestionRefId,
        setResolvedIata,
        setResolvedCityCode,
        setLocationDisplay,
        setLocationType,
        setIsResolving,
        setResolvedPickupQuery,
        setPickupDesk
      ),
    [resolveAirport]
  );

  const handleDropoffSuggestionClick = useCallback(
    (suggestion: SearchSuggestion | null) =>
      resolveAirport(
        suggestion,
        dropoffSuggestionRefId,
        setResolvedDropoffIata,
        setResolvedDropoffCityCode,
        setDropoffLocationDisplay,
        setDropoffLocationType,
        setIsResolvingDropoff,
        setResolvedDropoffQuery,
        () => undefined
      ),
    [resolveAirport]
  );

  // --- One-way toggle ---

  const handleOneWayToggle = useCallback(() => {
    setOneWay((prev) => {
      if (prev) {
        ++dropoffSuggestionRefId.current;
        setResolvedDropoffIata(null);
        setResolvedDropoffCityCode(null);
        setResolvedDropoffQuery(null);
        setDropoffLocationDisplay("");
        setDropoffLocationType("Airport");
        setIsResolvingDropoff(false);
        setError(null);
      }
      return !prev;
    });
  }, []);

  const handleDropoffFocus = useCallback(() => {
    setOneWay(true);
  }, []);

  // --- Clear location ---

  const clearPickupLocation = useCallback(() => {
    ++suggestionRefId.current;
    setPickupDesk(null);
    setPickupArea(null);
    setIsResolving(false);
    setLocationDisplay("");
    setResolvedIata(null);
    setResolvedCityCode(null);
    setResolvedPickupQuery(null);
  }, []);

  const clearDropoffLocation = useCallback(() => {
    ++dropoffSuggestionRefId.current;
    setIsResolvingDropoff(false);
    setDropoffLocationDisplay("");
    setResolvedDropoffIata(null);
    setResolvedDropoffCityCode(null);
    setResolvedDropoffQuery(null);
  }, []);

  // --- Date change ---

  const handleDateChange = useCallback(
    (newDates: DateRangeWithFlexibleData) => {
      setDates(newDates);
    },
    []
  );

  // Applied criteria are the mount baseline. Closing an editor restores all
  // fields and invalidates pending location resolutions and POST completions.
  const baseline = useRef({
    resolvedIata,
    resolvedCityCode,
    resolvedPickupQuery,
    pickupDesk,
    pickupArea,
    locationType,
    locationDisplay,
    oneWay,
    resolvedDropoffIata,
    resolvedDropoffCityCode,
    resolvedDropoffQuery,
    dropoffLocationType,
    dropoffLocationDisplay,
    dates,
    pickupTime,
    dropoffTime,
  });
  const discardDraft = useCallback(() => {
    ++suggestionRefId.current;
    ++dropoffSuggestionRefId.current;
    ++submitGeneration.current;
    submittingRef.current = false;
    setIsSubmitting(false);
    setIsResolving(false);
    setIsResolvingDropoff(false);
    setError(null);
    const saved = baseline.current;
    setResolvedIata(saved.resolvedIata);
    setResolvedCityCode(saved.resolvedCityCode);
    setResolvedPickupQuery(saved.resolvedPickupQuery);
    setPickupDesk(saved.pickupDesk);
    setPickupArea(saved.pickupArea);
    setLocationType(saved.locationType);
    setLocationDisplay(saved.locationDisplay);
    setOneWay(saved.oneWay);
    setResolvedDropoffIata(saved.resolvedDropoffIata);
    setResolvedDropoffCityCode(saved.resolvedDropoffCityCode);
    setResolvedDropoffQuery(saved.resolvedDropoffQuery);
    setDropoffLocationType(saved.dropoffLocationType);
    setDropoffLocationDisplay(saved.dropoffLocationDisplay);
    setDates(saved.dates);
    setPickupTime(saved.pickupTime);
    setDropoffTime(saved.dropoffTime);
    const discount =
      seededOwner.current === getVehicleSearchOwner()
        ? initialDiscount.current
        : undefined;
    setDiscountCode(discount?.code ?? "");
    setCodeVendorCodes(
      discount?.vendorCodes ?? (discount ? [discount.vendorCode] : [])
    );
  }, [setDiscountCode, setCodeVendorCodes]);
  useEffect(() => {
    if (seededOwner.current !== owner) {
      initialDiscount.current = undefined;
      discardDraft();
      seededOwner.current = owner;
    }
  }, [owner, discardDraft]);

  // --- Search submission ---

  const handleSubmit = useCallback(
    async (e?: React.FormEvent): Promise<boolean> => {
      e?.preventDefault();
      if (
        submittingRef.current ||
        seededOwner.current !== getVehicleSearchOwner()
      )
        return false;
      setError(null);
      const code = codeRef.current.trim();
      const selectedCompanies = Array.from(new Set(vendorsRef.current));
      if (code && selectedCompanies.length === 0) {
        setError("Choose at least one rental company for your code.");
        return false;
      }
      const discount = code
        ? {
            code,
            vendorCode: selectedCompanies[0],
            vendorCodes: selectedCompanies,
            vendorName: selectedCompanies
              .map(
                (value) =>
                  RENTAL_VENDOR_OPTIONS.find((vendor) => vendor.value === value)
                    ?.label ?? value
              )
              .join(" / "),
          }
        : undefined;

      if (isResolving || isResolvingDropoff) return false;
      if (!resolvedIata) {
        setError("Please select a pickup location from the suggestions");
        return false;
      }
      if (oneWay && !resolvedDropoffIata) {
        setError("Please select a drop-off location from the suggestions");
        return false;
      }

      const pickupDate = dates.date?.startDateStr;
      const dropoffDate = dates.date?.endDateStr;
      if (!pickupDate || !dropoffDate) {
        setError("Please select pickup and return dates");
        return false;
      }

      if (sameDayDisabled && pickupDate <= format(new Date(), "yyyy-MM-dd")) {
        setError(
          "Same-day rental car reservations are temporarily unavailable. Please choose tomorrow or later."
        );
        return false;
      }

      const generation = ++submitGeneration.current;
      const isLatestIntent = beginVehicleSearchIntent(
        sourceSearchIdRef.current ?? "new-search"
      );
      submittingRef.current = true;
      setIsSubmitting(true);

      try {
        const pickupDatetime = new Date(`${pickupDate}T${pickupTime}:00`);
        const dropoffDatetime = new Date(`${dropoffDate}T${dropoffTime}:00`);

        if (dropoffDatetime <= pickupDatetime) {
          setError("Return date/time must be after pickup date/time");
          setIsSubmitting(false);
          return false;
        }

        const isAirport = locationType === "Airport";
        const pickupLocation = isAirport
          ? resolvedIata
          : resolvedCityCode || resolvedIata;

        const searchBody: Record<string, unknown> = {
          pickupLocation,
          pickupLocationType: isAirport ? "Airport" : "City",
          // Send as a naive local-time string (no timezone), not UTC.
          // .toISOString() would convert to UTC and shift the time for users in non-UTC zones.
          // The backend stores these as naive datetimes representing the user's intended local time.
          pickupDatetime: `${pickupDate}T${pickupTime}:00`,
          dropoffDatetime: `${dropoffDate}T${dropoffTime}:00`,
          passengerCount: 2,
          test: searchAndBookTestEnv,
        };

        // GON-5981: send the resolved geo as pickupQuery so the backend's unified
        // search resolves the precise point and surfaces city desks. pickupLocation
        // stays as the resolved fallback (still required). resolvedPickupQuery is
        // only ever non-null for non-airport selections (cleared on airport picks),
        // so it alone is the signal — independent of a possibly-stale locationType
        // on a seeded header re-search.
        if (resolvedPickupQuery) {
          searchBody.pickupQuery = resolvedPickupQuery;
        }
        if (pickupDesk) {
          searchBody.pickupVendorCode = pickupDesk.vendorCode;
          searchBody.pickupVendorLocationId = pickupDesk.vendorLocationId;
        }

        if (oneWay && resolvedDropoffIata) {
          const isDropoffAirport = dropoffLocationType === "Airport";
          searchBody.dropoffLocation = isDropoffAirport
            ? resolvedDropoffIata
            : resolvedDropoffCityCode || resolvedDropoffIata;
          searchBody.dropoffLocationType = isDropoffAirport
            ? "Airport"
            : "City";
          if (resolvedDropoffQuery) {
            searchBody.dropoffQuery = resolvedDropoffQuery;
          }
        }

        const previousContext = sourceSearchIdRef.current
          ? readVehicleSearchContext(sourceSearchIdRef.current)
          : undefined;
        if (discount) {
          searchBody.codeVendorCodes = discount.vendorCodes ?? [
            discount.vendorCode,
          ];
          searchBody.corporateDiscountNumber = discount.code;
          searchBody.promotionalCode = discount.code;
        }
        const res = await fetch("/api/vehicle/search", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(searchBody),
        });

        if (generation !== submitGeneration.current || !isLatestIntent()) {
          if (generation === submitGeneration.current) setIsSubmitting(false);
          return false;
        }
        if (!res.ok) {
          setError("Failed to start vehicle search. Please try again.");
          setIsSubmitting(false);
          return false;
        }

        const data: VehicleSearchResponse = await res.json();
        if (generation !== submitGeneration.current || !isLatestIntent()) {
          if (generation === submitGeneration.current) setIsSubmitting(false);
          return false;
        }
        setIsSubmitting(false);

        // Thread pickup/dropoff location types through the URL so the results
        // page can reconstruct the original search when re-searching with a
        // discount code (VehicleSearchResponse doesn't return these fields).
        // We also thread the human-readable location names: the response only
        // returns the resolved IATA code (e.g. "LAX"), so without these the
        // header search bar would show "LAX" instead of what the user typed
        // ("Los Angeles"). The name never reaches the backend, so the URL is
        // the only place to carry it across to the results page.
        const urlParams = new URLSearchParams();
        urlParams.set("pickupLocationType", isAirport ? "Airport" : "City");
        if (locationDisplay)
          urlParams.set("pickupLocationName", locationDisplay);
        // GON-5981: carry the geo query so a header re-search / discount re-post
        // reproduces the same unified city-desk search (the response doesn't
        // return it, so the URL is the only place to thread it).
        if (resolvedPickupQuery)
          urlParams.set("pickupQuery", resolvedPickupQuery);
        if (pickupDesk) {
          urlParams.set("pickupVendorCode", pickupDesk.vendorCode);
          urlParams.set("pickupVendorLocationId", pickupDesk.vendorLocationId);
        }
        if (oneWay && resolvedDropoffIata) {
          const isDropoffAirport = dropoffLocationType === "Airport";
          const dropoffLoc = isDropoffAirport
            ? resolvedDropoffIata
            : resolvedDropoffCityCode || resolvedDropoffIata;
          if (dropoffLoc) urlParams.set("dropoffLocation", dropoffLoc);
          urlParams.set(
            "dropoffLocationType",
            isDropoffAirport ? "Airport" : "City"
          );
          if (dropoffLocationDisplay)
            urlParams.set("dropoffLocationName", dropoffLocationDisplay);
          if (resolvedDropoffQuery)
            urlParams.set("dropoffQuery", resolvedDropoffQuery);
        }
        const qs = urlParams.toString();
        const resultPath = `/car/search/${data.searchId}${qs ? `?${qs}` : ""}`;

        saveVehicleSearchContext(data.searchId, {
          ...previousContext,
          corporateOnly: discount
            ? previousContext?.corporateOnly ?? false
            : false,
          request: searchBody,
          resultsPath: resultPath,
          discount,
        });
        if (discount || previousContext?.discount) {
          posthog?.capture("vehicle_code_search_started", {
            search_id: data.searchId,
            code_present: !!discount,
            vendor_codes: discount?.vendorCodes ?? [],
          });
        }
        if (onSearchCompleteRef.current) {
          onSearchCompleteRef.current(resultPath);
        } else {
          router.push(resultPath);
        }
        return true;
      } catch {
        if (generation !== submitGeneration.current || !isLatestIntent()) {
          if (generation === submitGeneration.current) setIsSubmitting(false);
          return false;
        }
        setError("Something went wrong. Please try again.");
        setIsSubmitting(false);
        return false;
      } finally {
        if (generation === submitGeneration.current)
          submittingRef.current = false;
      }
    },
    [
      resolvedIata,
      resolvedCityCode,
      resolvedPickupQuery,
      pickupDesk,
      isResolving,
      isResolvingDropoff,
      locationType,
      locationDisplay,
      oneWay,
      resolvedDropoffIata,
      resolvedDropoffCityCode,
      resolvedDropoffQuery,
      dropoffLocationType,
      dropoffLocationDisplay,
      dates,
      pickupTime,
      dropoffTime,
      router,
      posthog,
      sameDayDisabled,
    ]
  );

  return useMemo(
    () => ({
      // Location
      seedPickupSuggestions,
      loadPickupSuggestions,
      pickupArea,
      pickupBranch: pickupDesk,
      selectPickupBranch,
      clearPickupBranch,
      locationDisplay,
      isResolving,
      handleSuggestionClick,
      clearPickupLocation,

      // One-way / dropoff
      oneWay,
      dropoffLocationDisplay,
      isResolvingDropoff,
      handleDropoffSuggestionClick,
      handleOneWayToggle,
      handleDropoffFocus,
      clearDropoffLocation,

      // Dates & times
      dates,
      pickupTime,
      dropoffTime,
      setPickupTime,
      setDropoffTime,
      handleDateChange,

      // Optional corporate search draft. Owner changes mask it immediately.
      discountCode: owner === seededOwner.current ? discountCode : "",
      codeVendorCodes: owner === seededOwner.current ? codeVendorCodes : [],
      setDiscountCode,
      setCodeVendorCodes,
      discardDraft,
      // Submission
      isSubmitting,
      error,
      handleSubmit,
    }),
    [
      seedPickupSuggestions,
      loadPickupSuggestions,
      pickupArea,
      pickupDesk,
      selectPickupBranch,
      clearPickupBranch,
      locationDisplay,
      isResolving,
      handleSuggestionClick,
      clearPickupLocation,
      oneWay,
      dropoffLocationDisplay,
      isResolvingDropoff,
      handleDropoffSuggestionClick,
      handleOneWayToggle,
      handleDropoffFocus,
      clearDropoffLocation,
      dates,
      pickupTime,
      dropoffTime,
      setPickupTime,
      setDropoffTime,
      handleDateChange,
      isSubmitting,
      error,
      handleSubmit,
      owner,
      discountCode,
      codeVendorCodes,
      setDiscountCode,
      setCodeVendorCodes,
      discardDraft,
    ]
  );
}
